import React from 'react';
import ReactDOM from 'react-dom';
import moment from "moment";

// 3rd party
import Loader from 'calcite-react/Loader';
import ClockIcon from 'calcite-ui-icons-react/ClockIcon';
import PinIcon from 'calcite-ui-icons-react/PinIcon';
import XIcon from 'calcite-ui-icons-react/XIcon';
import CheckCircleIcon from "calcite-ui-icons-react/CheckCircleIcon";
import BookingCancel from '../More/Actions/BookWorkspace/BookingCancel';
import PencilIcon from "calcite-ui-icons-react/PencilIcon";
import { getMeetingBookingSystem, getMeetingBookingSystemType} from '../More/Actions/BookWorkspace/WorkspaceReservation/workspaceReservationUtil';

// aiim.base
import ItemReference from '../../../aiim/base/ItemReference';

// aiim.datasets
import FieldNames from '../../../aiim/datasets/FieldNames';

// aiim.util
import { findFieldName, getAttributeValue } from '../../../aiim/util/aiimUtil';

// common.components.Modal
import { ModalController } from '../../../common/components/Modal';

// components.main.More.Actions.BookWorkspace.WorkspaceReservation
import { cancelBooking } from '../More/Actions/BookWorkspace/WorkspaceReservation/OfficeHotelingInterface';
import * as workspaceReservationUtil from '../More/Actions/BookWorkspace/WorkspaceReservation/workspaceReservationUtil';

// components.main.Events
import { getZulu } from '../Events/dateUtil';

// components.util
import * as component from '../../util/component';

// context
import Context from '../../../context/Context';
import Topic from '../../../context/Topic';

// util
import { makeEventInfo } from '../../../util/calendarUtil';
import CalendarSelector from '../../../util/CalendarSelector';
import Icons from "../../util/Icons";
import { isNetworkError } from '../../../util/networkUtil';
import { stringFormatter } from '../../../util/formatUtil';

const CSS = {
  bookingCardList: "i-booking-card-list",
  bookingCard: "i-booking-card",
  bookingInfoLine: "i-booking-info-line",
  locationDescription: "i-booking-location-description",
  flexRow: "i-flex-row",
  addToCalendar: "i-booking-add-calendar",
  cancel: "i-booking-cancel",
  edit: "i-booking-edit",
  popoutInner: "i-popout-inner i-meeting-cancel-confirmed",
  popoutOuter: "i-popout-outer",
  panelHeader: "i-infopanel-header i-panel-header",
  panelHeaderTools: "i-panel-header-tools",
  cancelConfirmed: "i-meeting-cancel-confirmed-body",
  meetingTitle: "i-meeting-cancel-confirmed--title",
  actionGroup: "i-action-group",
  bookingDescription: "i-booking-description"
};

export default class MyMeetingRooms extends React.Component {

  updateICSfile = false;

  constructor(props) {
    super(props);
    this.state = component.newState({
      bookings: [],
      isWorking: true
    });
  }

  componentDidMount() {
    this._getUserReservations();
    Topic.subscribe(Topic.RenderMeetingRooms, async(origResAttr) => {
      this.setState({isWorking: true})
      this.updateICSfile=true;
      await this._getUserReservations();
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.bookingDateFilter !== this.props.bookingDateFilter) {
      this.setState({isWorking: true});
      this._getUserReservations();
    }
  }
  
  componentWillUnmount() {
    component.componentWillUnmount(this);
  }

  _getUserReservations = async () => {
    const reservations = Context.getInstance().aiim.datasets.reservations;
    if (!reservations) {
      this.setState({ isWorking: false });
      return;
    }

    // Get logged in username
    const username = Context.getInstance().user.getUsername();
    const { reservedForUsernameField, endTimeField, stateField } = reservations;

    if (!username || !reservedForUsernameField || !endTimeField || !stateField) {
      this.setState({ isWorking: false });
      return;
    }

    try {
      const now = getZulu(new Date());
      const whereClauses = [
        `${reservedForUsernameField} = '${username}'`,
        `${endTimeField} > TIMESTAMP '${now}'`,
        `${stateField} = 1`
      ];
      if (this.props.bookingDateFilter && this.props.bookingDateFilter.where) {
        whereClauses.push(this.props.bookingDateFilter.where);
      }
      const myReservations = await reservations.query(whereClauses.join(" AND "));
      const bookings = (myReservations && myReservations.features) || [];
      const meetingRoomBookings = await this._filterMeetingReservations(bookings);

      this.setState({
        bookings: meetingRoomBookings,
        isWorking: false
      });
      return;
    } catch(e) {
      console.error("Unable to query for user reservations", e);
      this.setState({
        bookings: [],
        isWorking: false
      });

      if (e && isNetworkError(e.message)) {
        const i18n = Context.getInstance().i18n;
        Topic.publishNetworkError(i18n.meetingRooms.issues.m7);
      } else {
        Topic.publishErrorAccessingData();
      }
    }
  }

  _filterMeetingReservations = async (bookings) => {
    if (!bookings || !bookings.length) {
      return [];
    }

    const unitsDataset = Context.getInstance().aiim.datasets.units;
    const unitFields = unitsDataset.layer2D && unitsDataset.layer2D.fields;
    const meetingRoomBookings = [];

    // Gather the unit IDs from the reservations to make a query
    const unitIds = bookings
      .map((booking) => {
        const bookingAttrs = booking.attributes;
        const unitId = getAttributeValue(bookingAttrs, FieldNames.UNIT_ID);
        return `'${unitId}'`;
      });
    // To make sure we don't add duplicate ids to the query
    // Ex: UNIT_ID IN ('1', '1', '2', '2') -> UNIT_ID IN ('1', '2')
    const uniqueIds = [...new Set(unitIds)];

    const reservationMethodField = findFieldName(unitFields, FieldNames.RESERVATION_METHOD);
    const whereClauses = [
      `${unitsDataset.uidField} IN (${uniqueIds.join(",")})`,
      `${reservationMethodField} = 1`
    ];

    // Check if there is an AREA_ID field on the layer
    const areaIdField = findFieldName(unitFields, FieldNames.AREA_ID);
    if (areaIdField) {
      whereClauses.push(`(${areaIdField} = '' OR ${areaIdField} IS NULL)`);
    }
    const where = `${whereClauses.join(" AND ")}`;

    try {
      const results = await unitsDataset.query(where);
      const units = (results && results.features) || [];
      const meetingRoomUnitIds = units.map((unit) => getAttributeValue(unit.attributes, FieldNames.UNIT_ID));

      // Find the bookings that have meeting room unit IDs
      bookings.forEach((booking) => {
        const bookingAttrs = booking.attributes;
        const unitId = getAttributeValue(bookingAttrs, FieldNames.UNIT_ID);
        if (meetingRoomUnitIds.includes(unitId)) {
          const unit = units.find((unit) => getAttributeValue(unit.attributes, FieldNames.UNIT_ID) === unitId)
          meetingRoomBookings.push({ booking, unit });
        }
      });
    } catch(e) {
      console.error(e);
    }

    return meetingRoomBookings;
  }

  _sortBookings = (bookings) => {
    return bookings.sort((resA, resB) => {
      const attrsA = resA.booking.attributes;
      const attrsB = resB.booking.attributes;

      const startTimeA = getAttributeValue(attrsA, FieldNames.START_TIME);
      const startTimeB = getAttributeValue(attrsB, FieldNames.START_TIME);

      return startTimeA > startTimeB ? 1 : startTimeA < startTimeB ? -1 : 0;
    });
  }

  _makeBookingList = (bookings) => {
    const sortedBookings = this._sortBookings(bookings);
    return sortedBookings.map((data, i) => {
      return (
        <MyMeetingRoomsCard
          key={i}
          data={data}
          refresh={this._refreshList}
          updateICSfile={this.updateICSfile}
        />
      );
    });
  }

  _refreshList = () => {
    this.setState({ isWorking: true });
    this._getUserReservations();
  }

  renderBookingCards() {
    const i18n = Context.getInstance().i18n;
    const { bookings } = this.state;
    const bookingList = this._makeBookingList(bookings);

    return bookingList && bookingList.length > 0 ? (
      <ul className={CSS.bookingCardList}>
        {bookingList}
      </ul>
    ) : (
      <div style={{ padding: '0.7rem 1rem' }}>{i18n.meetingRooms.noBookings}</div>
    );
  }

  renderWorking() {
    return (<Loader sizeRatio={1} style={{ marginTop: '3rem' }} />);
  }

  render() {
    const { isWorking } = this.state;
    return isWorking ? this.renderWorking() : this.renderBookingCards();
  }
}

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

class MyMeetingRoomsCard extends React.Component {
  _componentId;
  _itemRef;

  constructor(props) {
    super(props);
    this.state = component.newState({
      popoverOpen: false
    });
    this._componentId = component.nextId("meeting-room-");
    this._itemRef = React.createRef();
  }

  async componentDidMount() {
    const booking = this.props.data && this.props.data.booking;
    const unitId = getAttributeValue(booking.attributes, FieldNames.UNIT_ID);
    let unitFeature = await workspaceReservationUtil.queryUnitByUnitId(unitId);
    if (unitFeature && unitFeature.features && unitFeature.features.length > 0) {
      unitFeature = unitFeature.features[0];
      this.setState({
        unitFeature: unitFeature
      })
    }
    component.own(this, [
      Topic.subscribe(Topic.InfoPanelClosed, params => {
        if (this._componentId === params.componentId) {
          this._focus();
        }
      })
    ]);
  }

  _focus() {
    if (this._itemRef && this._itemRef.current && this._itemRef.current.focus) {
      this._itemRef.current.focus();
    }
  }

  _makeItem = () => {
    const { data } = this.props;
    const { unit } = data;
    const units = Context.getInstance().aiim.datasets.units
    const source = units.getSource();

    const item = new ItemReference();
    item.fromSearchResult(source.key, { feature: unit, name: source.getTitle(unit) });
    return item;
  }

  _bookingCardClicked = () => {
    const item = this._makeItem();
    Topic.publish(Topic.ShowSearchResult, {
      sourceKey: item.sourceKey,
      searchResult: item.searchResult,
      zoom: true,
      highlight: true,
      trackRecent: true,
      isMeetingRoomsPanel: true,
      componentId: this._componentId
    });
  }

  _cancelClicked = (event) => {
    // Prevent the click event from propagating to the card
    event.stopPropagation();
    this._showCancelPopup(false);
  }

  _showCancelPopup = (retry) => {
    const bookingSystem = getMeetingBookingSystem();
    const i18n = Context.getInstance().i18n;
    const { data: { booking: { attributes } = {} } } = this.props;
    const unitName = getAttributeValue(attributes,FieldNames.UNIT_NAME);
    let msg = i18n.meetingRooms.cancel.message;
    if (unitName) {
      msg = i18n.meetingRooms.cancel.messagePattern.replace("{unitName}",unitName);
    }
    const message = retry
      ? i18n.meetingRooms.cancel.retry
      : msg;

    const options = {
      title: i18n.meetingRooms.cancel.modalTitle,
      message,
      okLabel: i18n.general.yes,
      cancelLabel: i18n.more.bookWorkspace.no,
      showOKCancel: true,
      closeOnOK: true,
      closeOnCancel: true
    };

    ModalController.confirm(options)
      .then((result) => {
        // User chose to continue canceling the meeting, proceed
        if (result && result.ok) {
          cancelBooking(bookingSystem, attributes)
            .then(() => {
              // Refresh the list of booked meetings
              if (typeof this.props.refresh === "function") {
                this.props.refresh();
              }
              // Show a confirmation
              this._renderCancelConfirmed(attributes);
            })
            .catch((e) => {
              console.error("Couldn't cancel meeting", e);

              if (e && isNetworkError(e.message)) {
                const i18n = Context.getInstance().i18n;
                const template = i18n.meetingRooms.issues.m9;
                const message = stringFormatter(template, { unit: unitName });
                Topic.publishNetworkError(message);
              } else {
                Topic.publishErrorUpdatingData();
              }

              // Give the user an option to try again
              this._showCancelPopup(true);
            });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  _renderCancelConfirmed(attributes) {
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);   
    const node = document.createElement("div");
    const onClose = () => {
      if (node && node.parentNode) {
        node.parentNode.removeChild(node);
        ReactDOM.unmountComponentAtNode(node)
      }
    };

    document.body.appendChild(node);
    ReactDOM.render(
      <BookingCancel 
        bookingSystemType={getMeetingBookingSystemType()}
        unitName={unitName}
        closePopup={onClose}
        data={this.props.data}        
      />, node);
  }

  _onKeyDown = (event) => {
    const ENTER = 13;
    const SPACE = 32;
    if (event.keyCode !== ENTER && event.keyCode !== SPACE) {
      return;
    }

    // Prevent propagation from children keyboard events
    const dataId = event.target.getAttribute("data-id");
    if (dataId === "card") {
      event.preventDefault();
      this._bookingCardClicked();
    }
  }

  _togglePopover = () => {
    this.setState({ popoverOpen: !this.state.popoverOpen });
  }

  render() {
    const i18n = Context.getInstance().i18n;
    const { data } = this.props;
    const { booking, unit } = data;
    const { popoverOpen } = this.state;
    const reservations = Context.getInstance().aiim.datasets.reservations;
    const levels = Context.getInstance().aiim.datasets.levels;
    const attributes = booking.attributes;
    const floorField = reservations.levelIdField;

    // Field values to display
    const title = getAttributeValue(attributes, FieldNames.TITLE);
    const startTime = getAttributeValue(attributes, FieldNames.START_TIME);
    const endTime = getAttributeValue(attributes, FieldNames.END_TIME);
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);

    // Level/Facility info
    const levelId = getAttributeValue(attributes, floorField);
    const levelData = levels.getLevelData(levelId);
    const { levelName, facilityName } = levelData;
    const description = `${unitName}, ${levelName}, ${facilityName}`;

    // Format a date string from the start and end times
    const mStartTime = moment(startTime);
    const mEndTime = moment(endTime);
    const isAllDay = getAttributeValue(attributes, FieldNames.ALL_DAY);
    const formattedDate = workspaceReservationUtil.formatDate(mStartTime, mEndTime, isAllDay);

    const addToCalendarButton = (
      <button
        className={CSS.addToCalendar}
        data-id={"calendar"}
        onClick={this._togglePopover}
      >
        {Icons.calendarPlus()}
        {i18n.general.add}
      </button>
    );

    const reservationsDataset = Context.getInstance().aiim.datasets.reservations;
    const unitsDataset = Context.getInstance().aiim.datasets.units;
    const calendarParams = makeEventInfo(reservationsDataset, booking, unitsDataset, unit);

    return (
      <li>
        <div className={CSS.bookingCard}>
          <div
            id={this._componentId}
            className={CSS.bookingDescription}
            ref={this._itemRef}
            role="button"
            tabIndex="0"
            data-id={"card"}
            onClick={this._bookingCardClicked}
            onKeyDown={this._onKeyDown}
          >
            <h3>{title}</h3>
            <div className={CSS.bookingInfoLine}>
              <ClockIcon />
              <h4>{formattedDate}</h4>
            </div>
            <div className={CSS.bookingInfoLine}>
              <PinIcon />
              <h4>{description}</h4>
            </div>
          </div>
          <div className={CSS.actionGroup}>
            <div style={{ width: 'calc(100%/3' }}></div>
            <CalendarSelector
              onRequestClose={this._togglePopover}
              open={popoverOpen}
              targetEl={addToCalendarButton}
              params={calendarParams}
              _updateICSfile={this.props.updateICSfile}
            />
            <button
              className={CSS.edit}
              data-id={"edit"}
              onClick={this._scheduleClicked}
            >
              <PencilIcon/>
              {i18n.more.bookWorkspace.checkInOut.rescheduleBooking.caption}
            </button>
            <button
              className={CSS.cancel}
              data-id={"cancel"}
              onClick={this._cancelClicked}
            >
              <XIcon />
              {i18n.general.cancel}
            </button>
          </div>
        </div>
      </li>
    );
  }

  _rescheduleBooking = async(when, isOngoingBooking)=> {
    const unit = this.props && this.props.data && this.props.data.unit;
    const resFeature = this.props && this.props.data && this.props.data.booking;
    let objectIdField;
    const reservations = Context.instance.aiim.datasets && Context.instance.aiim.datasets.reservations;
    if(reservations && reservations.layer2D && reservations.layer2D.objectIdField) {
      objectIdField = reservations.layer2D.objectIdField
    }

    const details = {
      when,
      isOngoingBooking,
      unitAttr: unit && unit.attributes,
      unitFeature: this.state.unitFeature,
      originalReservation: { attributes: resFeature && resFeature.attributes, booking: null },
      checkAvailability: this.checkAvailability,
      selfBooking: true,
      bookingType: "meetingRooms",
      objectID: getAttributeValue(resFeature.attributes, objectIdField)
    };
    workspaceReservationUtil.rescheduleBooking(details);
  }

  _scheduleClicked =async()=> {
    const resFeature = this.props && this.props.data && this.props.data.booking;
    const details = {
      reservation: resFeature,
      rescheduleBooking: this._rescheduleBooking,
      operation: "updateBookingTime",
      bookingType: "meetingRooms"
    }
    workspaceReservationUtil.showScheduleViewEsri(details);
  }
}

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

class CancelConfirmed extends React.Component {
  render() {
    const { attributes } = this.props;
    const i18n = Context.getInstance().i18n;
    const meetingTitle = getAttributeValue(attributes, FieldNames.TITLE);

    return (
      <div className={CSS.popoutOuter}>
        <div className={CSS.popoutInner}>
          <div className={CSS.panelHeader}>
            <div className={CSS.flexRow}>
              <h2>{i18n.meetingRooms.cancel.success}</h2>
            </div>
            <div className={CSS.panelHeaderTools}>
              <button
                ref={this.closeRef}
                title={i18n.general.close}
                aria-label={i18n.infoPanel.closeAriaLabel}
                onClick={this.props.closePopup}
              >
                {Icons.close()}
              </button>
            </div>
          </div>
          <div className={CSS.cancelConfirmed}>
            <CheckCircleIcon color="green" />
            <p>{i18n.meetingRooms.cancel.confirmation}</p>
            <p className={CSS.meetingTitle}>{meetingTitle}</p>
          </div>
        </div>
      </div>
    );
  }
}
